'use client';

import React, { useState } from 'react';
import { codexCategories, codexEntries, kavrisNodes, CodexEntry } from '@/data/kavrisData';
import StatusBadge from '@/components/ui/StatusBadge';
import AuthorityBadge from '@/components/ui/AuthorityBadge';
import DemoBadge from '@/components/ui/DemoBadge';
import { BookOpen, Clock, Link2 } from 'lucide-react';

export default function CodexWorkspace() {
  const [activeCategory, setActiveCategory] = useState<string>('Principles');
  const [selectedEntry, setSelectedEntry] = useState<CodexEntry>(codexEntries[0]);

  const filteredEntries = codexEntries.filter(e => e.category === activeCategory);
  const displayEntry = filteredEntries.find(e => e.id === selectedEntry?.id) || filteredEntries[0] || selectedEntry;

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    const first = codexEntries.find(e => e.category === cat);
    if (first) setSelectedEntry(first);
  };

  return (
    <div className="flex h-full" style={{ minHeight: 'calc(100vh - 56px)' }}>
      {/* Left nav */}
      <div className="w-52 xl:w-60 border-r border-border bg-card flex-shrink-0 overflow-y-auto">
        <div className="p-4 border-b border-border">
          <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground mb-1">KAVRIS → Codex</div>
          <h1 className="text-base font-semibold text-foreground">Codex</h1>
        </div>

        <nav className="py-3 px-2">
          <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground px-2 mb-2">
            Categories
          </div>
          {codexCategories.map(cat => {
            const count = codexEntries.filter(e => e.category === cat).length;
            return (
              <button
                key={`codex-cat-${cat}`}
                onClick={() => handleCategoryChange(cat)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded text-sm transition-all duration-150 mb-0.5 ${
                  activeCategory === cat
                    ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <span className="font-medium">{cat}</span>
                {count > 0 && (
                  <span className="text-[10px] font-mono-data bg-muted/50 px-1.5 py-0.5 rounded text-muted-foreground">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Entry list */}
        {filteredEntries.length > 0 && (
          <div className="px-2 pb-4">
            <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground px-2 mb-2 mt-4">
              Entries
            </div>
            {filteredEntries.map(entry => (
              <button
                key={`codex-entry-${entry.id}`}
                onClick={() => setSelectedEntry(entry)}
                className={`w-full text-left px-3 py-2.5 rounded text-xs transition-all duration-150 mb-0.5 ${
                  displayEntry?.id === entry.id
                    ? 'bg-muted/60 text-foreground border border-border'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                }`}
              >
                <div className="font-medium truncate mb-1">{entry.title}</div>
                <StatusBadge status={entry.status} size="xs" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-y-auto min-w-0">
        {displayEntry ? (
          <CodexEntryView entry={displayEntry} />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center p-12">
            <BookOpen size={32} className="text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground">Select a category and entry from the left navigation</p>
          </div>
        )}
      </div>

      {/* Right context panel */}
      {displayEntry && (
        <CodexContextPanel entry={displayEntry} />
      )}
    </div>
  );
}

function CodexEntryView({ entry }: { entry: CodexEntry }) {
  const paragraphs = entry.content.split('\n\n').filter(Boolean);

  return (
    <div className="max-w-2xl mx-auto p-6 xl:p-8">
      {/* Entry header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <DemoBadge />
          <span className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground">
            {entry.category}
          </span>
        </div>

        <h2 className="text-2xl font-semibold text-foreground mb-4 leading-tight">{entry.title}</h2>

        <div className="flex flex-wrap items-center gap-2 pb-4 border-b border-border">
          <StatusBadge status={entry.status} size="md" />
          <AuthorityBadge authority={entry.authority} size="md" />
          <div className="flex items-center gap-1 ml-auto text-[11px] font-mono-data text-muted-foreground">
            <Clock size={11} />
            <span>{entry.lastUpdated}</span>
          </div>
          <span className="text-[11px] font-mono-data text-muted-foreground">{entry.version}</span>
        </div>
      </div>

      {/* Content */}
      <div className="prose prose-sm max-w-none">
        {paragraphs.map((para, i) => {
          if (para.startsWith('**') && para.endsWith('**')) {
            const clean = para.slice(2, -2);
            return (
              <p key={`para-${entry.id}-${i}`} className="text-sm font-semibold text-foreground mb-3">
                {clean}
              </p>
            );
          }
          if (para.includes('**')) {
            const parts = para.split(/\*\*(.*?)\*\*/g);
            return (
              <p key={`para-${entry.id}-${i}`} className="text-sm text-muted-foreground leading-relaxed mb-3">
                {parts.map((part, pi) =>
                  pi % 2 === 1
                    ? <strong key={`bold-${entry.id}-${i}-${pi}`} className="text-foreground font-semibold">{part}</strong>
                    : part
                )}
              </p>
            );
          }
          return (
            <p key={`para-${entry.id}-${i}`} className="text-sm text-muted-foreground leading-relaxed mb-3">
              {para}
            </p>
          );
        })}
      </div>

      {/* Metadata block */}
      <div className="mt-8 p-4 rounded-xl border border-border bg-muted/20 space-y-3">
        <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground">Entry Metadata</div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <div className="text-muted-foreground mb-0.5">Parent Element</div>
            <div className="text-foreground font-medium">{entry.parentElement}</div>
          </div>
          <div>
            <div className="text-muted-foreground mb-0.5">Framework Relationship</div>
            <div className="text-foreground font-medium">{entry.frameworkRelationship}</div>
          </div>
          <div>
            <div className="text-muted-foreground mb-0.5">Entry ID</div>
            <div className="font-mono-data text-muted-foreground">{entry.id}</div>
          </div>
          <div>
            <div className="text-muted-foreground mb-0.5">Version</div>
            <div className="font-mono-data text-muted-foreground">{entry.version}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CodexContextPanel({ entry }: { entry: CodexEntry }) {
  const relatedAtlasNodes = kavrisNodes.filter(n => entry.relatedAtlasNodes.includes(n.id));
  const relatedCodexEntries = codexEntries.filter(e => entry.relatedCodexEntries.includes(e.id));

  return (
    <div className="w-64 xl:w-72 border-l border-border bg-card flex-shrink-0 overflow-y-auto hidden xl:block">
      <div className="p-4 border-b border-border">
        <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground">Context Panel</div>
      </div>

      <div className="p-4 space-y-6">
        {/* Related in Atlas */}
        <div>
          <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground mb-3">
            Related in Atlas
          </div>
          <div className="flex flex-col gap-1.5">
            {relatedAtlasNodes.map(node => (
              <div key={`ctx-atlas-${node.id}`} className="flex items-center gap-2 p-2 rounded border border-border bg-muted/10 text-xs">
                <Link2 size={10} className="text-primary/50 flex-shrink-0" />
                <span className="text-foreground flex-1 truncate">{node.label}</span>
                <StatusBadge status={node.status} size="xs" showDot={false} />
              </div>
            ))}
          </div>
        </div>

        {/* Parent */}
        <div>
          <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground mb-2">
            Parent Element
          </div>
          <div className="p-2 rounded border border-border bg-muted/10 text-xs text-foreground">
            {entry.parentElement}
          </div>
        </div>

        {/* Related Codex entries */}
        {relatedCodexEntries.length > 0 && (
          <div>
            <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground mb-3">
              Related Codex Entries
            </div>
            <div className="flex flex-col gap-1.5">
              {relatedCodexEntries.map(rel => (
                <div key={`ctx-codex-${rel.id}`} className="p-2 rounded border border-border bg-muted/10 text-xs">
                  <div className="text-foreground font-medium truncate">{rel.title}</div>
                  <div className="flex items-center gap-1 mt-1">
                    <StatusBadge status={rel.status} size="xs" />
                    <span className="text-muted-foreground font-mono-data">{rel.id}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Framework relationship */}
        <div>
          <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground mb-2">
            Framework Relationship
          </div>
          <div className="p-2 rounded border border-border bg-muted/10 text-xs text-muted-foreground leading-relaxed">
            {entry.frameworkRelationship}
          </div>
        </div>

        <div className="pt-2 border-t border-border">
          <DemoBadge label="DEMO entry" />
        </div>
      </div>
    </div>
  );
}